import { observer } from "mobx-react-lite";
import { parse } from "query-string";
import { useCallback, useContext } from "react";

import { ScrollablePane, SelectionMode, useTheme } from "@bps/fluent-ui";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import { ClaimStatuses } from "@libs/gateways/acc/AccGateway.dtos.ts";
import { QueryResult } from "@libs/utils/promise-observable/promise-observable.utils.ts";
import { ClaimsScreenContext } from "@modules/acc/screens/claims/context/ClaimsScreenContext.ts";
import { Claim } from "@stores/acc/models/Claim.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { DefaultNoResultsTile } from "@ui-components/InfiniteScrollList/DefaultNoResultsTile.tsx";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";

import { ClaimDocumentViewerDialog } from "./ClaimDocumentViewerDialog.tsx";
import { ClaimsFilterQuery } from "./claims-filter/claims-filter.types.ts";
import { claimsListColumns } from "./claims-filter/claims-filter.utils.tsx";

export const claimsActionRequiredStatuses = [
  ClaimStatuses.Accredited,
  ClaimStatuses.Declined,
  ClaimStatuses.Error,
  ClaimStatuses.Incomplete,
  ClaimStatuses.NotVerified,
  ClaimStatuses.Ready,
  ClaimStatuses.GetStatusError,
  ClaimStatuses.AuthenticationError,
  ClaimStatuses.CommunicationError,
  ClaimStatuses.ValidationError,
  ClaimStatuses.ProcessingError
];

export const ClaimsListBase = observer(() => {
  const { selection } = useContext(ClaimsScreenContext);
  const { acc, routing } = useStores();
  const theme = useTheme();

  const search = useCallback(
    async (query: PagingOptions): Promise<QueryResult<Claim>> => {
      const searchQuery = routing.location.search;

      const filter: ClaimsFilterQuery = searchQuery
        ? parse(searchQuery, {
            parseBooleans: true
          })
        : {};

      const {
        includeDischarged,
        includePrivate,
        claimsActionRequired,
        ...restFilter
      } = filter;

      const result = await acc.fetchClaims({
        ...query,
        ...restFilter,
        claimStatusCode: claimsActionRequired
          ? claimsActionRequiredStatuses
          : restFilter.claimStatusCode,
        excludePrivate: includePrivate !== undefined ? !includePrivate : false,
        excludeDischarged: !includeDischarged
      });

      return result;
    },
    [acc, routing.location.search]
  );

  const searchString = routing.queryStringParam("claimNumber") || "";
  const claimNumberSearch = parse(routing.location.search).claimNumber;
  const patientLabel = `${usePatientLabel(true)}`;

  const columns = claimsListColumns({ searchString, patientLabel });

  return (
    <>
      <div style={{ position: "relative", flexGrow: 1 }}>
        <ScrollablePane
          styles={{
            root: {
              height: "100%"
            }
          }}
        >
          <InfiniteScrollList<Claim>
            onRenderNoResults={() => (
              <DefaultNoResultsTile
                defaultText={
                  claimNumberSearch
                    ? `No matches found for "${claimNumberSearch}"`
                    : undefined
                }
              />
            )}
            setKey="claims-list"
            refreshKey={acc.ui.recentlyUpdatedClaimKey}
            getItems={search}
            selectionMode={SelectionMode.multiple}
            selection={selection}
            displayDefaultCheckBox
            checkboxAutomationAttribute="claim-list-item-checkbox"
            onRenderRow={(props, defaultRender) => {
              if (!props || !defaultRender) {
                return null;
              }
              return defaultRender!({
                ...props,
                styles: {
                  root: {
                    "&.is-selected:hover": {
                      backgroundColor:
                        theme.semanticColors.listItemBackgroundChecked
                    }
                  }
                }
              });
            }}
            columns={columns}
          />
        </ScrollablePane>
      </div>
      <ClaimDocumentViewerDialog />
    </>
  );
});

export const ClaimsList = withFetch(
  x => [
    x.acc.ref.accidentLocations.load(),
    x.acc.ref.accidentScenes.load(),
    x.acc.ref.occupations.load(),
    x.acc.ref.sports.load()
  ],
  ClaimsListBase
);
